import { useId, useState, type ReactNode } from 'react';
import { Card, type CardPadding } from '../Card';
import './SummaryCard.css';

export interface SummaryCardProps {
  /** Space Mono uppercase label shown in the header. */
  label: string;
  /** Optional short status string shown on the right of the header. */
  meta?: string;
  /** Rich content for the right side of the header (e.g. a control
   *  cluster). Takes precedence over `meta` when both are provided. */
  headerRight?: ReactNode;
  /** Render a chevron toggle that collapses the body. */
  collapsible?: boolean;
  /** Card padding. Defaults to `md`. Pass `none` for edge-to-edge
   *  content (e.g. a full-bleed table) — the header stays padded. */
  padding?: CardPadding;
  /** Skip the padded `Card.Body` wrapper and render children directly.
   *  Use with `padding="none"` when the card owns a full-bleed table or
   *  its own internal padding. */
  bodyless?: boolean;
  className?: string;
  bodyClassName?: string;
  children: ReactNode;
}

/**
 * Labeled content card matching the Fleet viewer's summary-card language:
 * a Space Mono uppercase label header with an optional right-aligned meta
 * string or control cluster, over a bordered body. Shared across the
 * Fleet health cards and the Fleet connectivity view so the two read as
 * one system.
 */
export function SummaryCard({
  label,
  meta,
  headerRight,
  collapsible = false,
  padding = 'md',
  bodyless = false,
  className,
  bodyClassName,
  children,
}: SummaryCardProps) {
  const [collapsed, setCollapsed] = useState(false);
  const bodyId = useId();
  const rootClass = ['ui-summary-card', className].filter(Boolean).join(' ');
  const bodyClass = ['ui-summary-card__body', bodyClassName]
    .filter(Boolean)
    .join(' ');

  return (
    <Card padding={padding} className={rootClass}>
      <Card.Header className="ui-summary-card__header">
        <h3 className="ui-summary-card__label">
          {collapsible ? (
            <button
              type="button"
              className="ui-summary-card__toggle"
              aria-expanded={!collapsed}
              aria-controls={bodyId}
              onClick={() => setCollapsed((c) => !c)}
            >
              <SummaryCardChevron open={!collapsed} />
              {label}
            </button>
          ) : (
            label
          )}
        </h3>
        {headerRight && !collapsed ? (
          headerRight
        ) : meta ? (
          <span className="ui-summary-card__meta">{meta}</span>
        ) : null}
      </Card.Header>
      {!collapsed &&
        (bodyless ? (
          children
        ) : (
          <Card.Body id={bodyId} className={bodyClass}>
            {children}
          </Card.Body>
        ))}
    </Card>
  );
}

SummaryCard.displayName = 'SummaryCard';

function SummaryCardChevron({ open }: { open: boolean }) {
  return (
    <svg
      className={`ui-summary-card__chevron${
        open ? ' ui-summary-card__chevron--open' : ''
      }`}
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <polyline points="9 18 15 12 9 6" />
    </svg>
  );
}
